/**
 * OpenOrderDetailModal — Modal overlay showing line-level open order detail.
 * Groups by customer, order, or SKU; rows expand to show the level below.
 * Fetches data lazily on mount from the order-detail API endpoint.
 */

import { useEffect, useState, useMemo } from "react";
import {
  getOrderDetail,
  OrderDetailResponse,
  OrderDetail,
} from "../utils/PowerBIApiCall";
import { toCsv, downloadCsv, slugifyForFilename } from "../utils/csvExport";
import { formatCurrency, formatNumber as formatQty } from "../utils/format";

interface OpenOrderDetailModalProps {
  portcoId: string;
  onClose: () => void;
}

type SortField =
  | "tranId"
  | "customer"
  | "date"
  | "totalQuantity"
  | "totalAmount";
type SortDir = "asc" | "desc";
type GroupBy = "customer" | "order" | "sku";

const GROUP_BY_LABELS: Record<GroupBy, string> = {
  customer: "By Customer",
  order: "By Order",
  sku: "By SKU",
};

export function OpenOrderDetailModal({
  portcoId,
  onClose,
}: OpenOrderDetailModalProps) {
  const [data, setData] = useState<OrderDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(
    new Set(),
  );
  const [expandedSkus, setExpandedSkus] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy] = useState<GroupBy>("customer");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("totalAmount");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const result = await getOrderDetail(portcoId);
        if (!result) {
          setError("Open order detail is not available for this company.");
        } else {
          setData(result);
        }
      } catch (e) {
        setError(
          e instanceof Error ? e.message : "Failed to load order detail",
        );
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [portcoId]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    if (!data) return [];

    let orders = data.orders;

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      orders = orders.filter(
        (o) =>
          o.customer.toLowerCase().includes(q) ||
          o.tranId.toLowerCase().includes(q) ||
          o.lineItems.some(
            (li) =>
              li.itemName.toLowerCase().includes(q) ||
              li.itemId.toLowerCase().includes(q),
          ),
      );
    }

    // Sort
    const sorted = [...orders].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "tranId":
          cmp = a.tranId.localeCompare(b.tranId);
          break;
        case "customer":
          cmp = a.customer.localeCompare(b.customer);
          break;
        case "date":
          cmp = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case "totalQuantity":
          cmp = a.totalQuantity - b.totalQuantity;
          break;
        case "totalAmount":
          cmp = a.totalAmount - b.totalAmount;
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

    return sorted;
  }, [data, search, sortField, sortDir]);

  // Roll filtered orders up into per-customer groups. Orders inside each group
  // keep the table's current sort; groups are ordered by the same sort field
  // where it maps (amount/qty/customer), otherwise by total amount.
  const customerGroups = useMemo(() => {
    const groups = new Map<string, OrderDetail[]>();
    for (const order of filteredOrders) {
      const list = groups.get(order.customer);
      if (list) list.push(order);
      else groups.set(order.customer, [order]);
    }

    const result = Array.from(groups.entries()).map(([customer, orders]) => ({
      customer,
      orders,
      orderCount: orders.length,
      totalQuantity: orders.reduce((s, o) => s + o.totalQuantity, 0),
      totalAmount: orders.reduce((s, o) => s + o.totalAmount, 0),
    }));

    result.sort((a, b) => {
      let cmp = 0;
      if (sortField === "customer") cmp = a.customer.localeCompare(b.customer);
      else if (sortField === "totalQuantity")
        cmp = a.totalQuantity - b.totalQuantity;
      else cmp = a.totalAmount - b.totalAmount;
      return sortDir === "asc" ? cmp : -cmp;
    });

    return result;
  }, [filteredOrders, sortField, sortDir]);

  // Roll the filtered orders' line items up by SKU. Each group keeps the lines
  // it came from so the row can expand to show which orders drive it.
  const skuGroups = useMemo(() => {
    const groups = new Map<string, SkuGroup>();

    for (const order of filteredOrders) {
      for (const li of order.lineItems) {
        const key = li.itemId || li.itemName;
        const group = groups.get(key) || {
          key,
          itemId: li.itemId,
          itemName: li.itemName,
          totalQuantity: 0,
          totalAmount: 0,
          lines: [],
        };
        group.totalQuantity += li.quantity;
        group.totalAmount += li.amount;
        group.lines.push({
          tranId: order.tranId,
          customer: order.customer,
          dateFormatted: order.dateFormatted,
          quantity: li.quantity,
          amount: li.amount,
        });
        groups.set(key, group);
      }
    }

    let result = Array.from(groups.values());
    result.forEach((g) => g.lines.sort((a, b) => b.amount - a.amount));

    // A search naming a SKU narrows to that SKU; one naming a customer or
    // order matches no SKU, so leave the already order-filtered groups alone.
    const q = search.trim().toLowerCase();
    if (q) {
      const skuMatches = result.filter(
        (g) =>
          g.itemId.toLowerCase().includes(q) ||
          g.itemName.toLowerCase().includes(q),
      );
      if (skuMatches.length > 0) result = skuMatches;
    }

    // Only qty/amount map onto a SKU group; anything else sorts by SKU name.
    result.sort((a, b) => {
      let cmp = 0;
      if (sortField === "totalQuantity")
        cmp = a.totalQuantity - b.totalQuantity;
      else if (sortField === "totalAmount") cmp = a.totalAmount - b.totalAmount;
      else cmp = a.key.localeCompare(b.key);
      return sortDir === "asc" ? cmp : -cmp;
    });

    return result;
  }, [filteredOrders, search, sortField, sortDir]);

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(field === "customer" || field === "tranId" ? "asc" : "desc");
    }
  }

  function toggleOrder(tranId: string) {
    setExpandedOrder((prev) => (prev === tranId ? null : tranId));
  }

  function toggleCustomer(customer: string) {
    setExpandedCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(customer)) next.delete(customer);
      else next.add(customer);
      return next;
    });
  }

  function toggleSku(key: string) {
    setExpandedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Export the currently shown orders (respecting search + sort) as order-level
  // CSV — one row per order, matching the table's main rows.
  function handleExport() {
    if (!data || filteredOrders.length === 0) return;

    const today = new Date().toISOString().slice(0, 10);
    const slug = slugifyForFilename(data.portcoName);

    if (groupBy === "sku") {
      const skuRows = skuGroups.map((g) => [
        g.itemId,
        g.itemName,
        g.lines.length,
        g.totalQuantity,
        g.totalAmount,
      ]);
      downloadCsv(
        `open-orders-by-sku-${slug}-${today}.csv`,
        toCsv(
          ["SKU", "Item Name", "Order Lines", "Quantity", "Amount"],
          skuRows,
        ),
      );
      return;
    }

    const includeEntity = filteredOrders.some((o) => o.reportingEntity);
    const headers = [
      "Order ID",
      "Customer",
      "Date",
      ...(includeEntity ? ["Reporting Entity"] : []),
      "Quantity",
      "Amount",
    ];

    const rows = filteredOrders.map((order) => [
      order.tranId,
      order.customer,
      order.dateFormatted,
      ...(includeEntity ? [order.reportingEntity ?? ""] : []),
      order.totalQuantity,
      order.totalAmount,
    ]);

    downloadCsv(`open-orders-${slug}-${today}.csv`, toCsv(headers, rows));
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return null;
    return (
      <span className="ml-1 text-[9px]">{sortDir === "asc" ? "▲" : "▼"}</span>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-navy-950/40 backdrop-blur-[2px]" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-pop border border-gray-200/70 w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <svg
              className="w-5 h-5 text-emerald-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Open Order Detail
              </h2>
              {data && (
                <p className="text-sm text-gray-500 mt-0.5">
                  {data.portcoName}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-wmp-500 rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-500">Loading order detail...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                  <svg
                    className="w-5 h-5 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <p className="text-sm text-gray-700 font-medium">
                  Failed to load order detail
                </p>
                <p className="text-xs text-gray-500 mt-1">{error}</p>
              </div>
            </div>
          )}

          {data && !loading && (
            <>
              {/* Summary bar */}
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-6 text-sm">
                <div>
                  <span className="text-gray-500">Orders:</span>{" "}
                  <span className="font-semibold text-gray-900 tabular-nums">
                    {data.orderCount}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Total Qty:</span>{" "}
                  <span className="font-semibold text-gray-900 tabular-nums">
                    {formatQty(data.totalQuantity)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Total Amount:</span>{" "}
                  <span className="font-semibold text-emerald-600 tabular-nums">
                    {formatCurrency(data.totalAmount)}
                  </span>
                </div>
                <div className="flex-1" />
                {/* Group-by toggle */}
                <div className="flex items-center rounded-lg border border-gray-200/70 bg-white p-0.5 text-xs font-medium">
                  {(["customer", "order", "sku"] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setGroupBy(mode)}
                      className={`px-2.5 py-1 rounded-md transition-colors ${
                        groupBy === mode
                          ? "bg-wmp-500 text-white"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {GROUP_BY_LABELS[mode]}
                    </button>
                  ))}
                </div>
                {/* Search */}
                <div className="relative">
                  <svg
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search orders..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 pr-3 py-1.5 text-sm border border-gray-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-wmp-500/40 focus:border-wmp-500 w-48"
                  />
                </div>
                {/* Export CSV */}
                <button
                  onClick={handleExport}
                  disabled={filteredOrders.length === 0}
                  title={
                    groupBy === "sku"
                      ? "Export shown SKUs to CSV"
                      : "Export shown orders to CSV"
                  }
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200/70 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Export CSV
                </button>
              </div>

              {/* Order table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-wmp-50/70 border-b border-gray-200">
                      <th className="w-8 px-2 py-2.5" />
                      {groupBy === "sku" ? (
                        <>
                          <th
                            className="text-left text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 cursor-pointer hover:text-gray-700 select-none"
                            onClick={() => handleSort("tranId")}
                          >
                            SKU
                            <SortIcon field="tranId" />
                          </th>
                          <th className="text-left text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5">
                            Item Name
                          </th>
                          <th className="text-left text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5">
                            Order Lines
                          </th>
                        </>
                      ) : groupBy === "order" ? (
                        <>
                          <th
                            className="text-left text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 cursor-pointer hover:text-gray-700 select-none"
                            onClick={() => handleSort("tranId")}
                          >
                            Order ID
                            <SortIcon field="tranId" />
                          </th>
                          <th
                            className="text-left text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 cursor-pointer hover:text-gray-700 select-none"
                            onClick={() => handleSort("customer")}
                          >
                            Customer
                            <SortIcon field="customer" />
                          </th>
                          <th
                            className="text-left text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 cursor-pointer hover:text-gray-700 select-none"
                            onClick={() => handleSort("date")}
                          >
                            Date
                            <SortIcon field="date" />
                          </th>
                        </>
                      ) : (
                        <>
                          <th
                            className="text-left text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 cursor-pointer hover:text-gray-700 select-none"
                            onClick={() => handleSort("customer")}
                          >
                            Customer
                            <SortIcon field="customer" />
                          </th>
                          <th className="text-left text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5">
                            Orders
                          </th>
                          <th className="px-4 py-2.5" />
                        </>
                      )}
                      <th
                        className="text-right text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 cursor-pointer hover:text-gray-700 select-none"
                        onClick={() => handleSort("totalQuantity")}
                      >
                        Qty
                        <SortIcon field="totalQuantity" />
                      </th>
                      <th
                        className="text-right text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 cursor-pointer hover:text-gray-700 select-none"
                        onClick={() => handleSort("totalAmount")}
                      >
                        Amount
                        <SortIcon field="totalAmount" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-8 text-center text-sm text-gray-400"
                        >
                          {search
                            ? "No orders match your search."
                            : "No open orders found."}
                        </td>
                      </tr>
                    )}
                    {groupBy === "order" &&
                      filteredOrders.map((order) => (
                        <OrderRow
                          key={order.tranId}
                          order={order}
                          expanded={expandedOrder === order.tranId}
                          onToggle={() => toggleOrder(order.tranId)}
                        />
                      ))}
                    {groupBy === "sku" &&
                      skuGroups.map((group) => (
                        <SkuGroupRow
                          key={group.key}
                          group={group}
                          expanded={expandedSkus.has(group.key)}
                          onToggle={() => toggleSku(group.key)}
                        />
                      ))}
                    {groupBy === "customer" &&
                      customerGroups.map((group) => (
                        <CustomerGroupRow
                          key={group.customer}
                          group={group}
                          expanded={expandedCustomers.has(group.customer)}
                          onToggle={() => toggleCustomer(group.customer)}
                          expandedOrder={expandedOrder}
                          onToggleOrder={toggleOrder}
                        />
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50">
          <p className="text-xs text-gray-400">
            {groupBy === "sku" ? (
              <>
                {skuGroups.length} SKU{skuGroups.length !== 1 ? "s" : ""} across{" "}
                {filteredOrders.length} order
                {filteredOrders.length !== 1 ? "s" : ""}
              </>
            ) : (
              <>
                {filteredOrders.length} order
                {filteredOrders.length !== 1 ? "s" : ""} shown
                {search && data ? ` (of ${data.orderCount} total)` : ""}
              </>
            )}
          </p>
          <button
            onClick={onClose}
            className="text-sm px-4 py-1.5 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

interface SkuGroup {
  key: string;
  itemId: string;
  itemName: string;
  totalQuantity: number;
  totalAmount: number;
  lines: {
    tranId: string;
    customer: string;
    dateFormatted: string;
    quantity: number;
    amount: number;
  }[];
}

/**
 * SKU group header row with expand/collapse. Expanded, it lists the order
 * lines that make up the SKU's open-order total.
 */
function SkuGroupRow({
  group,
  expanded,
  onToggle,
}: {
  group: SkuGroup;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className="border-b border-gray-100 last:border-b-0 hover:bg-wmp-50/50 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-2 py-3 text-center">
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? "rotate-90" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </td>
        <td className="px-4 py-3 text-sm font-mono text-gray-700">
          {group.itemId}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900">
          {group.itemName}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500 tabular-nums">
          {group.lines.length} line{group.lines.length !== 1 ? "s" : ""}
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums">
          {formatQty(group.totalQuantity)}
        </td>
        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right tabular-nums">
          {formatCurrency(group.totalAmount)}
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={6} className="px-0 py-0 bg-wmp-50/30">
            <div className="ml-10 mr-4 my-2">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="microlabel text-left px-3 py-1.5">
                      Order ID
                    </th>
                    <th className="microlabel text-left px-3 py-1.5">
                      Customer
                    </th>
                    <th className="microlabel text-left px-3 py-1.5">Date</th>
                    <th className="microlabel text-right px-3 py-1.5">Qty</th>
                    <th className="microlabel text-right px-3 py-1.5">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {group.lines.map((line, idx) => (
                    <tr
                      key={`${group.key}-${line.tranId}-${idx}`}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <td className="px-3 py-1.5 text-xs font-mono text-gray-500">
                        {line.tranId}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-gray-700">
                        {line.customer}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-gray-600">
                        {line.dateFormatted}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-gray-600 text-right tabular-nums">
                        {formatQty(line.quantity)}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-gray-700 text-right tabular-nums">
                        {formatCurrency(line.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Customer group header row with expand/collapse. When expanded, renders the
 * customer's orders as nested OrderRows (which in turn expand to line items).
 */
function CustomerGroupRow({
  group,
  expanded,
  onToggle,
  expandedOrder,
  onToggleOrder,
}: {
  group: {
    customer: string;
    orders: OrderDetail[];
    orderCount: number;
    totalQuantity: number;
    totalAmount: number;
  };
  expanded: boolean;
  onToggle: () => void;
  expandedOrder: string | null;
  onToggleOrder: (tranId: string) => void;
}) {
  return (
    <>
      <tr
        className="border-b border-gray-100 last:border-b-0 hover:bg-wmp-50/50 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-2 py-3 text-center">
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? "rotate-90" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </td>
        <td className="px-4 py-3 text-sm font-semibold text-gray-900">
          {group.customer}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500 tabular-nums">
          {group.orderCount} order{group.orderCount !== 1 ? "s" : ""}
        </td>
        <td className="px-4 py-3" />
        <td className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums">
          {formatQty(group.totalQuantity)}
        </td>
        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right tabular-nums">
          {formatCurrency(group.totalAmount)}
        </td>
      </tr>

      {expanded &&
        group.orders.map((order) => (
          <OrderRow
            key={order.tranId}
            order={order}
            expanded={expandedOrder === order.tranId}
            onToggle={() => onToggleOrder(order.tranId)}
            indent
          />
        ))}
    </>
  );
}

/**
 * Single order row with expand/collapse for line items.
 */
function OrderRow({
  order,
  expanded,
  onToggle,
  indent = false,
}: {
  order: OrderDetail;
  expanded: boolean;
  onToggle: () => void;
  indent?: boolean;
}) {
  return (
    <>
      <tr
        className={`border-b border-gray-50 last:border-b-0 hover:bg-wmp-50/50 transition-colors cursor-pointer ${indent ? "bg-gray-50/40" : ""}`}
        onClick={onToggle}
      >
        <td className={`px-2 py-3 text-center ${indent ? "pl-6" : ""}`}>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? "rotate-90" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </td>
        <td className="px-4 py-3 text-sm font-mono text-gray-700">
          {order.tranId}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900">
          {order.customer}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          {order.dateFormatted}
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums">
          {formatQty(order.totalQuantity)}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right tabular-nums">
          {formatCurrency(order.totalAmount)}
        </td>
      </tr>

      {/* Expanded line items */}
      {expanded && (
        <tr>
          <td colSpan={6} className="px-0 py-0 bg-wmp-50/30">
            <div className="ml-10 mr-4 my-2">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="microlabel text-left px-3 py-1.5">
                      Item ID
                    </th>
                    <th className="microlabel text-left px-3 py-1.5">
                      Item Name
                    </th>
                    <th className="microlabel text-right px-3 py-1.5">Qty</th>
                    <th className="microlabel text-right px-3 py-1.5">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {order.lineItems.map((li, idx) => (
                    <tr
                      key={`${order.tranId}-${li.itemId}-${idx}`}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <td className="px-3 py-1.5 text-xs font-mono text-gray-500">
                        {li.itemId}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-gray-700">
                        {li.itemName}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-gray-600 text-right tabular-nums">
                        {formatQty(li.quantity)}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-gray-700 text-right tabular-nums">
                        {formatCurrency(li.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
